import type { JSX } from "react";

import type { GrantStatus, MintFailure, SessionView } from "../api/bench";

interface IdentityPanelProps {
  session: SessionView;
  /** Switch the fallback identity. Rows are the selector; there is no dropdown elsewhere. */
  onSelect: (profile: string) => void;
  busy: boolean;
}

/** What to do about each failure mode, in the tester's terms rather than the transport's. */
const FAILURE_HEADLINE: Record<MintFailure, string> = {
  route_absent: "This deployment does not mint tokens",
  not_configured: "Token minting is not configured",
  keycloak_unreachable: "Keycloak is unreachable",
  unknown_profile: "The Memory Service does not know this identity",
  ams_unreachable: "The Memory Service is unreachable",
  unexpected: "Token minting failed unexpectedly",
};

/** What each identity is for. The roles beside it are the fact; this is the plain-English gloss. */
const PROFILE_SCOPE: Record<string, string> = {
  me: "your own account, with whatever roles it was given",
  "agent-user": "the everyday caller: memories, recall, ask, entities, code, sessions, ingest",
  "system-user": "a delivery producer: lifecycle events, and refused everywhere else",
  "admin-user": "everything agent-user reaches, plus project-memory reset",
  "editor-user":
    "everything agent-user reaches, plus superseding a locked memory another agent created",
};

/**
 * Whose token the next call carries.
 *
 * Every call goes out as the identity selected here, including the ones the service will refuse.
 * The bench used to substitute the identity an operation required, so a lifecycle event quietly
 * went out as `system-user` however this was set — which made every call succeed and therefore
 * tested nothing. Testing that AMS refuses the wrong token means being able to send the wrong
 * token.
 *
 * `me` is the signed-in tester's own account, present only after a sign-in, and the only identity
 * a deployment without the dev-token route can offer.
 *
 * These rows are the selector. There was a dropdown in the title bar saying the same thing twice.
 */
export function IdentityPanel({ session, onSelect, busy }: IdentityPanelProps): JSX.Element {
  return (
    <section className="panel">
      <header className="panel__header">
        <h2>Identities</h2>
        <span className="muted">every call goes out as the one selected</span>
      </header>

      {session.header_only && (
        <div className="notice notice--warning">
          <strong>No token was minted.</strong> Calls will carry scope headers only. That works
          against a deployment running with authentication disabled, and fails with 401 against
          one that has it on.
        </div>
      )}

      <ul className="grants">
        {session.grants.map((grant) => {
          const selected = grant.profile === session.profile;
          return (
            <li key={grant.profile} className={grant.available ? "" : "grants__item--failed"}>
              <button
                type="button"
                className={`grants__choice${selected ? " grants__choice--selected" : ""}`}
                // An unavailable identity cannot be selected: its token was never minted, so
                // choosing it would only produce 401s with no explanation.
                disabled={busy || !grant.available || selected}
                aria-pressed={selected}
                onClick={() => onSelect(grant.profile)}
              >
                <span className="grants__head">
                  <code>{grant.profile}</code>
                  {grant.label && <span className="grants__who">{grant.label}</span>}
                  {selected && <span className="flag flag--on">selected</span>}
                  {grant.available ? (
                    <span className="muted">valid for {formatMinutes(grant.seconds_remaining)}</span>
                  ) : (
                    <span className="flag flag--danger">unavailable</span>
                  )}
                </span>
                <span className="muted">{PROFILE_SCOPE[grant.profile] ?? "Seeded dev identity."}</span>
                {grant.available && (
                  // The roles are what actually decide, so they belong on screen rather than
                  // implied by the identity's name -- especially for `me`, whose roles are
                  // whatever an administrator gave the account.
                  <span className="grants__roles">
                    {grant.roles.length ? (
                      grant.roles.map((role) => (
                        <code key={role}>{role}</code>
                      ))
                    ) : (
                      <em className="muted">no roles</em>
                    )}
                  </span>
                )}
              </button>
              {!grant.available && <GrantFailure grant={grant} />}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function GrantFailure({ grant }: { grant: GrantStatus }): JSX.Element {
  const headline = grant.failure ? FAILURE_HEADLINE[grant.failure] : "Unavailable";
  return (
    <p className="grants__failure">
      <strong>{headline}.</strong> {grant.detail}
    </p>
  );
}

function formatMinutes(seconds: number | null): string {
  if (seconds === null) {
    return "an unknown time";
  }
  const minutes = Math.round(seconds / 60);
  return minutes <= 1 ? "under a minute" : `about ${minutes} minutes`;
}
